import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import { toast } from 'sonner';
import './SubscriptionManagement.css';

function SubscriptionManagement() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, paused, cancelled
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getAllSubscriptions();
      setSubscriptions(data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Fehler beim Laden der Abonnements');
    } finally {
      setLoading(false);
    }
  };

  const handleSetTrial = async (userId) => {
    const confirmed = window.confirm('Möchten Sie diesen Benutzer auf "Trial" setzen?');
    if (!confirmed) return;

    try {
      await apiClient.setUserToTrial(userId);
      toast.success('Benutzer auf Trial gesetzt');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCancelSubscription = async (userId, reason = 'Cancelled by admin') => {
    const confirmed = window.confirm('Möchten Sie dieses Abonnement wirklich kündigen?');
    if (!confirmed) return;

    try {
      await apiClient.cancelUserSubscription(userId, reason);
      toast.success('Abonnement gekündigt');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleReactivateSubscription = async (userId) => {
    try {
      await apiClient.reactivateUserSubscription(userId);
      toast.success('Abonnement reaktiviert');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Fehler: ' + (error.response?.data?.detail || error.message));
    }
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    // Filter by status
    if (filter !== 'all' && sub.status !== filter) return false;
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        sub.user_email?.toLowerCase().includes(query) ||
        sub.user_id?.toLowerCase().includes(query) ||
        sub.tier?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const getStatusBadge = (status) => {
    const badges = {
      active: { class: 'badge-success', text: 'Aktiv' },
      paused: { class: 'badge-warning', text: 'Pausiert' },
      cancelled: { class: 'badge-error', text: 'Gekündigt' },
      trial: { class: 'badge-info', text: 'Trial' },
      expired: { class: 'badge-default', text: 'Abgelaufen' }
    };
    const badge = badges[status] || badges.expired;
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  const getTierBadge = (tier) => {
    const tiers = {
      standard: { class: 'tier-standard', text: 'Standard' },
      premium: { class: 'tier-premium', text: 'Premium' },
      trial: { class: 'tier-trial', text: 'Trial' }
    };
    const tierBadge = tiers[tier] || tiers.standard;
    return <span className={`tier-badge ${tierBadge.class}`}>{tierBadge.text}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="subscription-management-loading">
        <div className="spinner"></div>
        <p>Lade Abonnements...</p>
      </div>
    );
  }

  return (
    <div className="subscription-management" data-testid="admin-subscription-management">
      <div className="subscription-header">
        <h2>📋 Abonnement-Verwaltung</h2>
        <p className="subscription-count">
          {filteredSubscriptions.length} von {subscriptions.length} Abonnements
        </p>
      </div>

      <div className="subscription-filters">
        <div className="filter-group">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Alle ({subscriptions.length})
          </button>
          <button
            className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Aktiv ({subscriptions.filter(s => s.status === 'active').length})
          </button>
          <button
            className={`filter-btn ${filter === 'paused' ? 'active' : ''}`}
            onClick={() => setFilter('paused')}
          >
            Pausiert ({subscriptions.filter(s => s.status === 'paused').length})
          </button>
          <button
            className={`filter-btn ${filter === 'cancelled' ? 'active' : ''}`}
            onClick={() => setFilter('cancelled')}
          >
            Gekündigt ({subscriptions.filter(s => s.status === 'cancelled').length})
          </button>
        </div>

        <div className="search-group">
          <input
            type="text"
            placeholder="Suche nach E-Mail, User ID oder Tier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {filteredSubscriptions.length === 0 ? (
        <div className="no-subscriptions">
          <p>Keine Abonnements gefunden.</p>
        </div>
      ) : (
        <div className="subscription-table-container">
          <table className="subscription-table">
            <thead>
              <tr>
                <th>Benutzer</th>
                <th>Tier</th>
                <th>Intervall</th>
                <th>Status</th>
                <th>Preis</th>
                <th>Gestartet</th>
                <th>Läuft ab</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.map((sub) => (
                <tr key={sub.id} data-testid={`subscription-row-${sub.id}`}>
                  <td>
                    <div className="user-cell">
                      <div className="user-email">{sub.user_email || 'Unbekannt'}</div>
                      <div className="user-id">{sub.user_id?.substring(0, 8)}...</div>
                    </div>
                  </td>
                  <td>{getTierBadge(sub.tier || sub.subscription_tier)}</td>
                  <td>
                    <span className="interval-badge">
                      {sub.billing_interval === 'monthly' ? 'Monatlich' : 'Jährlich'}
                    </span>
                  </td>
                  <td>{getStatusBadge(sub.status)}</td>
                  <td className="price-cell">
                    {sub.price_per_period ? `${sub.price_per_period.toFixed(2)}€` : '-'}
                  </td>
                  <td className="date-cell">{formatDate(sub.started_at)}</td>
                  <td className="date-cell">{formatDate(sub.current_period_end)}</td>
                  <td>
                    <div className="action-buttons">
                      {sub.status === 'active' && (
                        <>
                          <button
                            onClick={() => handleSetTrial(sub.user_id)}
                            className="btn-action btn-trial"
                            title="Auf Trial setzen"
                          >
                            🎯 Trial
                          </button>
                          <button
                            onClick={() => handleCancelSubscription(sub.user_id)}
                            className="btn-action btn-cancel"
                            title="Kündigen"
                          >
                            ❌ Kündigen
                          </button>
                        </>
                      )}
                      {sub.status === 'cancelled' && (
                        <button
                          onClick={() => handleReactivateSubscription(sub.user_id)}
                          className="btn-action btn-reactivate"
                          title="Reaktivieren"
                        >
                          ✅ Reaktivieren
                        </button>
                      )}
                      {sub.status === 'paused' && (
                        <button
                          onClick={() => handleReactivateSubscription(sub.user_id)}
                          className="btn-action btn-resume"
                          title="Fortsetzen"
                        >
                          ▶️ Fortsetzen
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default SubscriptionManagement;
